import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Typography, Button, TextField, Divider, Box, Dialog, Slide } from '@material-ui/core';
import { useAtualizarSacola, useObterSacola } from 'storage';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import classNames from 'classnames';
import { useStyles } from './visualizar-produto-modal-styles';
import { isEmpty } from 'lodash';
import { imagemBase64 } from 'utils/tratamento-de-imagem';
import { MovSimplesProdutoModel } from 'api/cardapio/models/produto/mov-simples-produto-model';
import { roundTo } from 'utils/round-to';
import { isValidUUIDV4 } from 'utils/is-valid-id';
import { useParams } from 'react-router-dom';
import { TemaModel } from 'api/cardapio/models/tema/tema-model';
import { EmpresaModel } from 'api/cardapio/models/empresa/empresa-model';
import { useThemeQueries } from 'components/dialogs/dialog-adicionar-produto-com-subitem/util-styles';
import { FecharIcon } from 'components/icons/fechar-icon';
import SemImagem from 'assets/img/sem-imagem.jpg';
import { TransitionProps } from '@material-ui/core/transitions';

export interface VisualizarProdutoModalProps {
  aberto: boolean;
  produto: MovSimplesProdutoModel;
  quandoForFechado: any;
  mesaId: string
  permiteEntrega: TemaModel['permiteEntrega'] | undefined
  permiteRetirada: TemaModel['permiteRetirada'] | undefined
  fone: EmpresaModel['fone'] | undefined
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide style={{ zIndex: 3000 }} direction="up" mountOnEnter unmountOnExit ref={ref} {...props} />;
});

export const VisualizarProdutoModal = ({
  aberto,
  produto,
  quandoForFechado,
  mesaId,
  permiteEntrega,
  permiteRetirada,
  fone
}: VisualizarProdutoModalProps) => {

  const classes = useStyles();
  const [quantidade, setQuantidade] = useState<number>(1);
  const [observacao, setObservacao] = useState<string>(produto.observacao);

  const { empresaId } = useParams<{ empresaId: string }>()
  const sacola = useObterSacola(empresaId)
  const itensSacola = isEmpty(sacola?.data) ? [] : sacola?.data as MovSimplesProdutoModel[]
  const [atualizarSacola] = useAtualizarSacola()
  const { isMobile } = useThemeQueries();
  const modalHistoryPushedRef = useRef(false);
  const closingFromPopRef = useRef(false);

  useEffect(() => {
    setQuantidade(produto.qCom === 0 ? 1 : produto.qCom)
    setObservacao(produto.observacao)
  }, [produto.observacao, produto.qCom])

  const hasItemSacola = itensSacola.find(x => x.id === produto.id)

  const valoresIniciais = useCallback(() => {
    setQuantidade(1);
    setObservacao('');
  }, []);

  // quando fechar
  const fechar = useCallback(() => {
    valoresIniciais();
    quandoForFechado();
    // Se o modal foi aberto com pushState e o fechamento foi MANUAL (não via popstate),
    // remova a entrada dummy com history.back(). Caso tenha vindo do popstate, não navegue.
    modalHistoryPushedRef.current = false;
    closingFromPopRef.current = false;
  }, [quandoForFechado, valoresIniciais]);

  const addOrEditSacola = useCallback(() => {
    let prod = produto

    prod.qCom = quantidade
    prod.observacao = observacao
    prod.vFinal = roundTo(prod.vUnCom * quantidade)
    atualizarSacola({ produto: prod, empresaId: empresaId })

    fechar()
  }, [produto, quantidade, observacao, atualizarSacola, empresaId, fechar]);

  const handleChangeQuantidade = useCallback(
    (operacao) => {
      switch (operacao) {
        case '+':
          setQuantidade(prev => prev += 1);
          break;

        case '-':
          setQuantidade(prev => prev -= 1);
          break;
      }
    },
    [],
  );
  const handleChangeObservacao = useCallback((event) => {
    setObservacao(event.target.value);
  }, []);

  const total = useCallback(() => {
    return ((produto?.vUnCom || 0) * quantidade).toLocaleString('pt-br', {
      style: 'currency',
      currency: 'BRL',
    });
  }, [produto?.vUnCom, quantidade]);

  const tratamentoImg = useMemo(() => {
    const produtoImgs = JSON.parse(localStorage.getItem('ProdutoImgs')!)
    if (!isEmpty(produto?.imgUrl)) {
      return imagemBase64(produto!.imgUrl)
    }
    const produtoImg = produtoImgs?.find((x: any) => x.produtoId === produto.produtoId)
    if ((produtoImg ?? '')?.length > 0) {
      return imagemBase64(produtoImg.imagemUrl)
    }
    return SemImagem
  }, [produto])

  const permiteEntregaRetirada = permiteEntrega || permiteRetirada

  const clienteEstaNaLoja = useCallback((): boolean => {
    //verificar se tem o mesaId e se é válido
    if (!isEmpty(mesaId) && isValidUUIDV4(mesaId))
      return true;

    return false;
  }, [mesaId]);

  const podeConfirmar = (fone || '').length >= 0 && (clienteEstaNaLoja() || permiteEntregaRetirada)


  // Intercept browser back when modal está aberto: empurra estado no histórico e fecha ao voltar
  useEffect(() => {
    if (!aberto) return;

    try {
      window.history.pushState({ modal: 'visualizar-produto' }, '', window.location.href);
      modalHistoryPushedRef.current = true;
    } catch { }

    const handlePopState = (event: PopStateEvent) => {
      closingFromPopRef.current = true;
      fechar();
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [aberto, fechar])

  return (
    <>
      <Dialog
        open={aberto}
        keepMounted
        maxWidth="md"
        onClose={fechar}
        fullScreen={isMobile}
        style={{
          zIndex: 3000,
        }}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
        TransitionComponent={Transition}
      >
        <div className={classes.container}>
          <div className={classes.content}>
            <Button variant="text" className={classes.closeButton} onClick={fechar}>
              <FecharIcon tipo="BUTTON" class={classes.closeIcon} />
            </Button>
            <div
              className={classes.imgContainer}
              style={{
                backgroundImage: `url(${tratamentoImg})`,
              }}
            ></div>

            <div className={classes.info}>
              <Box flex={1}>
                <Typography
                  variant="h5"
                  className={classes.title}
                >
                  {produto.xProd}
                </Typography>
                <Typography
                  variant="h6"
                  color="textPrimary"
                  style={{ fontWeight: 500 }}
                  gutterBottom
                >
                  <div>
                    {produto?.vUnCom.toLocaleString('pt-br', {
                      style: 'currency',
                      currency: 'BRL',
                    })}

                    <span style={{ fontSize: '.9rem', fontWeight: 700 }}> {produto?.uCom}</span>
                  </div>
                </Typography>

                {produto.infAdic && (
                  <Box mt={3}>
                    <Typography
                      variant='body1'
                      color="textSecondary"
                      style={{
                        fontSize: '14px',
                        lineHeight: '19px',
                      }}>
                      {produto.infAdic}
                    </Typography>
                  </Box>
                )}
              </Box>
              <div>
                {podeConfirmar ? (
                  <>
                    <TextField
                      className={classes.observacao}
                      InputLabelProps={{ shrink: true }}
                      label="Alguma observação?"
                      multiline
                      rows={4}
                      inputProps={{ maxLength: 150 }}
                      onChange={handleChangeObservacao}
                      value={observacao}
                      placeholder="Descreva sua observação."
                      variant="outlined"
                    />


                  </>
                ) : null}
              </div>
              {(fone || '').length >= 0 && (clienteEstaNaLoja() || permiteEntregaRetirada) ? (
                <div className={classes.footerContainer}>
                  <Divider style={{ margin: '.5rem 0rem' }} />

                  <div className={classes.btnsConfirmar}>
                    <div className={classes.seletor}>
                      <RemoveIcon
                        onClick={() => {
                          if (quantidade > 1)
                            handleChangeQuantidade('-')
                        }}
                        className={classNames(classes.remover, classes.btn, quantidade <= 1 && classes.disabled)}
                      />
                      <Typography variant="h6" color="textPrimary">
                        {quantidade}
                      </Typography>
                      <AddIcon
                        onClick={() => handleChangeQuantidade('+')}
                        className={classNames(classes.adicionar, classes.btn)}
                      />
                    </div>

                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.btnConfirmar}
                      onClick={() => addOrEditSacola()}
                      size="large"
                      disabled={quantidade <= 0}
                    >
                      <span className={classes.iconBasket}>
                        {hasItemSacola ? 'Editar' : 'Adicionar'}
                      </span>
                      {total()}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};